import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import moment from 'moment-timezone';
import useApiClient from '../../../hooks/api/useApiClient';
import {
  calculateTotals,
  countAndPercentageLabelFormatter,
  generateMonthRange,
  getCompanyCountsMap,
  getCompanyIdsMap,
  getPeriodsMap,
} from './chartHelpers';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

export default function PaymentsRatioTiersChart({ monthRange, salesManagers, onBarSelect }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:CustomerBase:customer_health_tab' });

  const [, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [totals, setTotals] = useState(null);

  const { CustomerBaseStatsManager } = useApiClient();

  useEffect(() => {
    fetchData();
  }, [monthRange, salesManagers]);

  const fetchData = async () => {
    setLoading(true);
    const { data, success } = await CustomerBaseStatsManager.getPaymentsRatioTiersStats({
      start_date: monthRange[0].format('YYYY-MM-DD'),
      end_date: monthRange[1].format('YYYY-MM-DD'),
      managers: salesManagers,
      client_timezone: moment.tz.guess(),
    });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    setTotals(calculateTotals(data));
    setData(data);
  };

  const chartData = {
    labels: data
      ? Object.keys(data['0_3.33']).map((date) => moment(date, 'YYYY-MM').format('MMM YYYY'))
      : generateMonthRange(monthRange),
    datasets: [
      {
        label: t('dataset_labels.negative', { values: '0 - 3.33' }),
        data: getCompanyCountsMap(data, '0_3.33'),
        backgroundColor: 'rgba(255, 99, 132, 0.7)',
        meta: {
          companyIds: getCompanyIdsMap(data, '0_3.33'),
          periods: getPeriodsMap(data, '0_3.33'),
        },
      },
      {
        label: t('dataset_labels.ok', { values: '3.33 - 5' }),
        data: getCompanyCountsMap(data, '3.33_5'),
        backgroundColor: 'rgba(255, 206, 86, 0.7)',
        meta: {
          companyIds: getCompanyIdsMap(data, '3.33_5'),
          periods: getPeriodsMap(data, '3.33_5'),
        },
      },
      {
        label: t('dataset_labels.good', { values: '5 - 10' }),
        data: getCompanyCountsMap(data, '5_10'),
        backgroundColor: 'rgba(75, 192, 192, 0.7)',
        meta: {
          companyIds: getCompanyIdsMap(data, '5_10'),
          periods: getPeriodsMap(data, '5_10'),
        },
      },
      {
        label: t('dataset_labels.excellent', { values: '10+' }),
        data: getCompanyCountsMap(data, '10+'),
        backgroundColor: 'rgba(54, 162, 235, 0.7)',
        meta: {
          companyIds: getCompanyIdsMap(data, '10+'),
          periods: getPeriodsMap(data, '10+'),
        },
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: t('titles.payments_ratio'),
      },
      datalabels: {
        formatter: (value, context) => countAndPercentageLabelFormatter(value, totals[context.dataIndex]),
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
    onClick: (event, elements) => {
      if (elements.length < 1) {
        return;
      }

      const { datasetIndex } = elements[0];
      const barIndex = elements[0].index;

      onBarSelect({
        companyIds: chartData.datasets[datasetIndex].meta.companyIds[barIndex],
        period: chartData.datasets[datasetIndex].meta.periods[barIndex],
        additionalParams: {
          with_payments_info: true,
        },
      });
    },
  };

  return (
    <div className="mt-2xl">
      <Bar data={chartData} options={chartOptions} />
    </div>
  );
}
